import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'
import useMultiRef from '@aytm/lib/hooks/useMultiRef'

const ENTER_TRANSITION = {}
const EXIT_TRANSITION = {}

RollDown.propTypes = {
  duration: PropTypes.number.isRequired,
  opened: PropTypes.bool.isRequired,
  children: PropTypes.func.isRequired,
  containerExpander: PropTypes.object
}
export default function RollDown({ children, opened, duration = 0, containerExpander } = {}) {
  const [savedStyles, fullHeight, transitionStarted, transitionTimeout] = useMultiRef({}, null, null, null)

  const nodeRef = useRef()
  useEffect(
    () => () => {
      if (containerExpander && containerExpander.expanded && nodeRef.current) {
        containerExpander.unexpand(nodeRef.current)
      }
    },
    [containerExpander]
  )

  const stopTransition = (node, transitionType) => {
    node.style.height = null != savedStyles.current.height ? savedStyles.current.height : ''
    delete savedStyles.current.height
    node.style.overflowY = null != savedStyles.current.overflowY ? savedStyles.current.overflowY : ''
    delete savedStyles.current.overflowY
    node.style.transition = null != savedStyles.current.transition ? savedStyles.current.transition : ''
    delete savedStyles.current.transition
    fullHeight.current = null
    transitionStarted.current = null
    if (containerExpander) {
      switch (transitionType) {
        case ENTER_TRANSITION: {
          nodeRef.current = node
          containerExpander.expand(node)
          break
        }
        case EXIT_TRANSITION: {
          nodeRef.current = null
          containerExpander.unexpand(node)
          break
        }
        default: {
          //
        }
      }
    }
  }

  const startTransition = (node, transitionType) => {
    if (transitionStarted.current !== transitionType) {
      if (transitionTimeout.current) {
        clearTimeout(transitionTimeout.current)
        transitionTimeout.current = null
      }
      stopTransition(node)
    }
    fullHeight.current = node.clientHeight
    savedStyles.current.height = node.style.height
    savedStyles.current.overflowY = node.style.overflowY
    savedStyles.current.transition = node.style.transition
    node.style.height = ENTER_TRANSITION === transitionType ? 0 : `${fullHeight.current}px`
    node.style.overflowY = 'hidden'
    node.style.transition = `height ${duration}ms`
    transitionStarted.current = transitionType
  }

  const transition = (node, transitionType) => {
    if (transitionTimeout.current) {
      clearTimeout(transitionTimeout.current)
    }
    transitionTimeout.current = setTimeout(() => {
      transitionTimeout.current = null
      if (document.contains(node)) {
        node.style.height = ENTER_TRANSITION === transitionType ? `${fullHeight.current}px` : 0
      }
    }, 0)
  }

  return (
    <Transition
      in={opened}
      timeout={duration}
      mountOnEnter={true}
      unmountOnExit={true}
      appear={true}
      onEnter={node => void startTransition(node, ENTER_TRANSITION)}
      onEntering={node => void transition(node, ENTER_TRANSITION)}
      onEntered={node => void stopTransition(node, ENTER_TRANSITION)}
      onExit={node => void startTransition(node, EXIT_TRANSITION)}
      onExiting={node => void transition(node, EXIT_TRANSITION)}
      onExited={node => void stopTransition(node, EXIT_TRANSITION)}
    >
      {state => children(state)}
    </Transition>
  )
}
